// GymRite design tokens: the single source of visual values for the UI refresh.
// Theme-ready: another theme is another object with the same keys, applied with applyTokens().
// Components never use these raw values directly in styles; they use `gr` (CSS variable
// references), so a theme switch repaints everything without a re-render.

export const tokens = {
  color: {
    bg: "#f4f8f9",
    surface: "#ffffff",
    tint: "#f0f9fa",
    line: "#e6ecee",
    line2: "#eef2f3",
    lineStrong: "#cfd9dd",
    ink: "#16252d",
    ink2: "#4d5c64",
    // Darkened from #6c7a81 so 12–13px hints pass WCAG AA (4.5:1) on white, bg and both chat bubbles.
    muted: "#5f6d74",
    brand: "#04abbb",
    // Text-safe teal: titles and solid buttons with white text (5.1:1 on white, 4.8:1 on bg). brand stays for icons/fills.
    brandStrong: "#027a86",
    brandDark: "#026670",
    brandSoft: "#e3f6f8",
    brandLight: "#38c9d4",
    onBrandGlass: "rgba(255,255,255,0.16)",
    onBrandGlassLine: "rgba(255,255,255,0.32)",
    onBrand: "#ffffff",
    // Dialog / drawer backdrop: ink at 48% (replaces the legacy dark gradient).
    overlay: "rgba(22,37,45,0.48)",
    navy: "#1e3366",
    ok: "#1c9b62",
    okSoft: "#e5f6ee",
    overdue: "#c03b3b",
    overdueSoft: "#fdecec",
    late: "#b7791f",
    lateSoft: "#fdf2df",
    danger: "#d64545",
    dangerSoft: "#fdecec",
  },
  font: {
    family: '"Poppins", sans-serif',
    // display / hero: logged-out brand side only (GrAuthLayout headline on large screens).
    size: { xs: 12, sm: 13, md: 14, lg: 16, xl: 18, xxl: 20, h2: 24, h1: 28, display: 40, hero: 52 },
    weight: { regular: 400, medium: 500, semibold: 600 },
    lineHeight: { tight: 1.25, normal: 1.5 },
  },
  radius: { chip: 8, control: 10, tile: 12, card: 20, pill: 999 },
  space: { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40, 12: 48 },
  control: { sm: 32, md: 40, touch: 44, lg: 48 },
  icon: { sm: 16, md: 18, lg: 20, xl: 24 },
  // Fixed sizes of layout pieces, so screens never invent their own: side drawer, file tiles, a feed
  // column that stays readable (community), its side column, and the top bar height (sticky offsets).
  layout: { drawer: 400, fileTile: 72, feed: 680, sideColumn: 320, topBar: 64 },
  shadow: {
    card: "0 2px 6px rgba(22,37,45,.04), 0 14px 34px rgba(22,37,45,.05)",
    raised: "0 8px 24px rgba(22,37,45,.12)",
    focus: "0 0 0 3px var(--gr-color-brand-soft)",
  },
};

const kebab = (s) => String(s).replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
const px = (n) => `${n}px`;

// Flat list of [cssVariableName, value, pathInTokens].
const entries = (t) => [
  ...Object.entries(t.color).map(([k, v]) => [
    `--gr-color-${kebab(k)}`,
    v,
    ["color", k],
  ]),
  ["--gr-font-family", t.font.family, ["font", "family"]],
  ...Object.entries(t.font.size).map(([k, v]) => [
    `--gr-font-size-${k}`,
    px(v),
    ["font", "size", k],
  ]),
  ...Object.entries(t.font.weight).map(([k, v]) => [
    `--gr-font-weight-${k}`,
    String(v),
    ["font", "weight", k],
  ]),
  ...Object.entries(t.font.lineHeight).map(([k, v]) => [
    `--gr-line-height-${k}`,
    String(v),
    ["font", "lineHeight", k],
  ]),
  ...Object.entries(t.radius).map(([k, v]) => [
    `--gr-radius-${k}`,
    px(v),
    ["radius", k],
  ]),
  ...Object.entries(t.space).map(([k, v]) => [
    `--gr-space-${k}`,
    px(v),
    ["space", k],
  ]),
  ...Object.entries(t.control).map(([k, v]) => [
    `--gr-control-${k}`,
    px(v),
    ["control", k],
  ]),
  ...Object.entries(t.icon).map(([k, v]) => [
    `--gr-icon-${k}`,
    px(v),
    ["icon", k],
  ]),
  ...Object.entries(t.layout).map(([k, v]) => [
    `--gr-layout-${kebab(k)}`,
    px(v),
    ["layout", k],
  ]),
  ...Object.entries(t.shadow).map(([k, v]) => [
    `--gr-shadow-${k}`,
    v,
    ["shadow", k],
  ]),
];

// { "--gr-color-brand": "#04abbb", ... } for applyTokens().
export const toCssVars = (t = tokens) =>
  Object.fromEntries(entries(t).map(([name, value]) => [name, value]));

// Same shape as `tokens`, but every leaf is "var(--gr-…)". Use this in sx / styled.
export const gr = entries(tokens).reduce((acc, [name, , path]) => {
  let node = acc;
  path.slice(0, -1).forEach((key) => {
    node[key] = node[key] || {};
    node = node[key];
  });
  node[path[path.length - 1]] = `var(${name})`;
  return acc;
}, {});
